import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import type { IConversation } from 'src/api/landlord-messages-api';
import { getBestChannelForConversation, type EConversationCategory, type IConversationMatchWithChannel } from 'src/pages/landlord/conversations/context/types';
import { type ILead } from 'src/services/api';
import {usePersistentState} from 'src/services/storage/use-persistent-state';

const getLink = (conversation: IConversation | IConversationMatchWithChannel, lead: ILead, backPath: string, showNavigation: boolean = true) => {
    const path = '/landlord/inner/messages/from-lead';
    const params = new URLSearchParams();

    params.set('lead_uuid', conversation.lead_uuid);

    const channel = getBestChannelForConversation(conversation, lead);
    params.set('channel', channel);

    if (showNavigation) {
        params.set('show_navigation', 'true');
    }

    // base URL is required but will not be used
    const backUrl = new URL(backPath, 'https://platform.reffie.me/');
    // delete back_path to avoid nesting
    backUrl.searchParams.delete('back_path');
    params.set('back_path', `${backUrl.pathname}?${backUrl.searchParams}`);
    return `${path}?${params.toString()}`;
};

export const useOpenConversation = (conversationsByCategory: Record<EConversationCategory, IConversation[]>, leadsByUuid: Map<string, ILead>) => {
    const navigate = useNavigate();

    const [, saveConversationsStorage] = usePersistentState(
        'conversations',
        [] as IConversation[],
        (value): value is IConversation[] => Array.isArray(value)
    );

    return useCallback(async (conversation: IConversation | IConversationMatchWithChannel, backPath: string) => {
        const lead = leadsByUuid.get(conversation.lead_uuid);
        if (!lead) {
            console.warn('Lead not found for conversation, refusing to open conversation', conversation);
            return;
        }
        let showNavigation = true;

        const currentCategory = Object.entries(conversationsByCategory).reduce((acc, curr) => {
            const _currentCategory = acc;
            const [category, conversationsFromCategory] = curr;
            const isCurrentCategory = conversationsFromCategory.some(o => o.lead_uuid === conversation.lead_uuid);

            if (isCurrentCategory) {
                return category as EConversationCategory;
            } else {
                return _currentCategory;
            }
        }, undefined as EConversationCategory | undefined);

        if (!currentCategory) {
            console.warn('Conversation category not found for this conversation, unable to facilitate navigation (but opening anyway)', conversation);
            showNavigation = false;
        } else {
            showNavigation = true;
            // This is for the purposes of navigation inside the messages view
            // For some reason react-router-7 stucks if anything before takes more than a tick, so better to await for it
            await saveConversationsStorage(conversationsByCategory[currentCategory]);
        }

        navigate(getLink(conversation, lead, backPath, showNavigation));
    }, [conversationsByCategory]);
};
